<template>
  <div
    class="tw-z-[20] tw-rounded-md tw-border-2 tw-border-system-warning-500 tw-bg-system-warning-50 tw-p-3"
    role="alert"
    data-test-id="sfi-migration-banner"
    v-if="visible && !isDismissed"
  >
    <div class="tw-flex tw-items-center tw-justify-between tw-gap-2">
      <div
        class="tw-flex tw-items-center tw-justify-center tw-rounded-md tw-bg-system-warning-100 tw-p-1"
      >
        <cb-icon-attention-critical
          color="currentColor"
          class="tw-text-system-warning-500"
        />
      </div>
      <div
        class="tw-flex-grow tw-overflow-hidden tw-whitespace-normal tw-break-words"
      >
        <div
          class="tw-text-lg tw-font-semibold"
          data-test-id="sfi-migration-banner-title"
        >
          {{ t("microsoft-permission-changes") }}
        </div>
      </div>
      <button
        class="tw-inline-flex"
        :aria-label="t('close')"
        data-test-id="sfi-migration-banner-close-button"
        @click="handleClose"
      >
        <cb-icon-close-2 color="currentColor" />
      </button>
    </div>
    <div
      class="tw-ml-10 tw-text-sm"
      data-test-id="sfi-migration-banner-message"
    >
      <div class="tw-flex tw-flex-col tw-gap-1.5">
        <span>{{ t("microsoft-permission-changes-description") }}</span>
        <div class="tw-flex tw-items-center tw-justify-end tw-gap-4">
          <a
            :href="moreInfoUrl"
            target="_blank"
            rel="noopener"
            class="tw-inline-block tw-rounded-md tw-border tw-border-primary-grey-200 tw-bg-white tw-px-3 tw-py-1.5 tw-font-semibold tw-text-primary-grey-900 tw-transition-colors hover:tw-bg-primary-grey-50"
            >Learn more</a
          >
          <router-link
            v-if="!isOnConnectionsPage"
            v-slot="{ href, navigate }"
            :to="{ name: connectionPageName }"
          >
            <a
              :href="href"
              @click="navigate"
              class="tw-inline-block tw-rounded-md tw-bg-primary-grey-900 tw-px-3 tw-py-1.5 tw-font-semibold tw-text-white tw-transition-colors hover:tw-bg-primary-grey-800"
              >Update permissions</a
            >
          </router-link>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from "vue";
import { useI18n } from "vue-i18n";
import { useRoute } from "vue-router";
import { useCookies } from "@vueuse/integrations/useCookies";
import { useFlag } from "@/composables/useFlags/useFlag";
import { useGlobalOrganisationID } from "@/composables/useGlobalOrganisationID";
import { getSfiMigrationBannerCookieName } from "@/constants/banner-cookie";
import { COOKIE_SETTINGS } from "@/utils/cookies";

const { t } = useI18n();
const { isFeatureAvailable: isConnectionsV2Available } =
  useFlag("CONNECTIONS_V2");
const { customerGlobalOrganisationId } = useGlobalOrganisationID();
const cookies = useCookies();
const route = useRoute();

type Props = {
  visible?: boolean;
};
withDefaults(defineProps<Props>(), {
  visible: false,
});

const moreInfoUrl =
  "https://helpcenter.titanhq.com/support/solutions/articles/4000232792";

const cookieName = computed(() =>
  getSfiMigrationBannerCookieName(customerGlobalOrganisationId.value),
);

const isDismissed = computed(() => Boolean(cookies.get(cookieName.value)));

const connectionPageName = computed(() =>
  isConnectionsV2Available.value
    ? "customer-settings-connections-v2"
    : "customer-settings-connections",
);

const isOnConnectionsPage = computed(
  () => route.name === connectionPageName.value,
);

const handleClose = () => {
  // COOKIE_SETTINGS pins the path (and a maxAge) so the dismissal applies across
  // sibling routes and can be removed by the logout cleanup, which reads/removes
  // cookies at the same path.
  cookies.set(cookieName.value, true, COOKIE_SETTINGS);
};
</script>

<style scoped></style>
